import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  PawIcon, StarIcon,
  ScissorsIcon, HotelIcon,
  GiftIcon, TagIcon, SparklesIcon, UserIcon, ArrowRightIcon,
  CheckCircleIcon, MapPinIcon, LockIcon, CalendarIcon, MailIcon,
  HeartIcon,
} from "./Icons";

/* ─── Data ─────────────────────────────────────────────────────────────── */

const testimonials = [
  { name: "Maria Santos",  pet: "Shih Tzu owner",          avatarColor: "#7C3AED", stars: 5, text: "Absolutely amazing! My dog Coco came home looking like a little princess. The staff are so gentle and loving — I wouldn't trust anyone else!" },
  { name: "Carlos Reyes",  pet: "Golden Retriever owner",  avatarColor: "#EC4899", stars: 5, text: "We've been coming here for 2 years now. The boarding is top-notch and I always feel at ease knowing Max is safe and happy here." },
  { name: "Ana Lim",       pet: "Persian Cat owner",       avatarColor: "#F97316", stars: 5, text: "My cat hates baths but the groomers here are so patient. She always comes back calm and beautifully groomed. 10/10 recommend!" },
];

/* Floating background SVG decorations */
const floatingDefs = [
  { Icon: PawIcon,      size: 36, delay: "0s"   },
  { Icon: ScissorsIcon, size: 28, delay: "1.0s" },
  { Icon: HeartIcon,    size: 26, delay: "2.0s" },
  { Icon: PawIcon,      size: 22, delay: "0.8s" },
  { Icon: ScissorsIcon, size: 30, delay: "1.5s" },
  { Icon: HeartIcon,    size: 24, delay: "0.4s" },
  { Icon: PawIcon,      size: 28, delay: "2.4s" },
  { Icon: PawIcon,      size: 20, delay: "1.2s" },
];
const floatColors = ["#7C3AED", "#EC4899", "#F97316", "#14B8A6"];




/* ─── Default Showcase Fallback Data ────────────────────────────────────── */
const defaultShowcaseItems = [
  { _id: "1", beforeImage: "/gallery-shih-tzu.jpg",    afterImage: "/images/cuts/teddy_bear_cut.png", petName: "Coco",   breed: "Shih Tzu",          service: "Full Grooming & Teddy Cut",  petType: "dog", description: "Matted scruffy coat transformed into a fluffy Teddy Bear style!" },
  { _id: "2", beforeImage: "/gallery-persian-cat.jpg", afterImage: "/images/cuts/cat_lion_cut.png",    petName: "Luna",   breed: "Persian Cat",        service: "Lion Cut & Deep Bath",        petType: "cat", description: "Tangly long fur professionally groomed into a sleek, majestic Lion Cut." },
  { _id: "3", beforeImage: "/gallery-golden.jpg",      afterImage: "/images/cuts/summer_cut.png",      petName: "Max",    breed: "Golden Retriever",   service: "De-shedding & Summer Trim",  petType: "dog", description: "Heavy undercoat thinned out and neatened up for maximum comfort." },
  { _id: "4", beforeImage: "/gallery-poodle.jpg",      afterImage: "/images/cuts/puppy_cut.png",       petName: "Bella",  breed: "Toy Poodle",         service: "Puppy Cut & Styling",        petType: "dog", description: "Overgrown curls sculpted into a fresh, bouncy Puppy Cut." },
  { _id: "5", beforeImage: "/gallery-tabby-cat.jpg",   afterImage: "/images/cuts/cat_summer_cut.png",  petName: "Mochi",  breed: "Tabby Kitten",       service: "Cat Summer Trim",            petType: "cat", description: "Sanitizing bath and hygienic trim keeping Mochi fresh and playful." },
  { _id: "6", beforeImage: "/gallery-husky.jpg",       afterImage: "/images/cuts/lion_cut.png",        petName: "Storm",  breed: "Siberian Husky",     service: "Full Grooming & Blowout",    petType: "dog", description: "Deep blowout, coat treatment, and neat trim for a picture-perfect look." },
];

/* ─── Before & After Transformation Gallery ────────────────────────────── */
function GallerySection({ setActiveTab }) {
  const [items, setItems] = useState(defaultShowcaseItems);
  const [filter, setFilter] = useState("all");
  const [activeViews, setActiveViews] = useState({}); // { [id]: 'before' | 'after' }

  useEffect(() => {
    let isMounted = true;
    axios.get("/api/showcases")
      .then(res => {
        if (isMounted && res.data.success && res.data.data?.length > 0) {
          setItems(res.data.data);
        }
      })
      .catch(err => {
        console.warn("Could not fetch live showcases, using default gallery:", err.message);
      });
    return () => { isMounted = false; };
  }, []);

  const filters = [
    { id: "all", label: "All Transformations", icon: "✨" },
    { id: "dog", label: "🐶 Dogs", icon: "🐶" },
    { id: "cat", label: "🐱 Cats", icon: "🐱" },
  ];

  const filtered = filter === "all"
    ? items
    : items.filter(g => (g.petType || "dog").toLowerCase() === filter);

  const toggleView = (id, mode) => {
    setActiveViews(prev => ({ ...prev, [id]: mode }));
  };

  return (
    <section id="gallery" style={{ padding: "6rem 1.5rem", background: "white", position: "relative" }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "3rem" }}>
          <span style={{
            display: "inline-block", background: "rgba(124,58,237,0.1)", color: "#7C3AED",
            fontSize: "0.8rem", fontWeight: 800, padding: "0.4rem 1.2rem", borderRadius: "999px",
            letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "1rem",
            boxShadow: "0 2px 10px rgba(124,58,237,0.1)",
          }}>
            ✨ Before &amp; After Transformations
          </span>
          <h2 style={{
            fontSize: "clamp(2rem, 4vw, 2.8rem)", fontWeight: 900, color: "#1f2937",
            margin: "0 0 1rem", letterSpacing: "-0.02em",
          }}>
            Real Makeovers,{" "}
            <span style={{ background: "linear-gradient(90deg,#7C3AED,#EC4899)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
              Happy Tails
            </span>
          </h2>
          <p style={{ color: "#6B7280", fontSize: "1rem", maxWidth: "520px", margin: "0 auto 2rem", lineHeight: 1.7 }}>
            See the incredible transformations from our expert grooming team. Toggle between Before &amp; After photos to see the magic!
          </p>

          {/* Filter tabs */}
          <div style={{ display: "flex", gap: "0.6rem", justifyContent: "center", flexWrap: "wrap" }}>
            {filters.map(f => (
              <button
                key={f.id}
                id={`gallery-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                style={{
                  padding: "0.55rem 1.4rem",
                  borderRadius: "999px",
                  border: "1.5px solid",
                  fontSize: "0.88rem",
                  fontWeight: 800,
                  cursor: "pointer",
                  transition: "all 0.25s ease",
                  borderColor: filter === f.id ? "#7C3AED" : "rgba(124,58,237,0.2)",
                  background: filter === f.id ? "linear-gradient(135deg,#7C3AED,#EC4899)" : "white",
                  color: filter === f.id ? "white" : "#7C3AED",
                  boxShadow: filter === f.id ? "0 4px 18px rgba(124,58,237,0.35)" : "none",
                  transform: filter === f.id ? "scale(1.03)" : "scale(1)",
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Transformation Cards Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "1.75rem",
          }}
          className="gallery-grid"
        >
          {filtered.map((item) => {
            const currentView = activeViews[item._id] || "after";
            const isShowingAfter = currentView === "after";

            return (
              <div
                key={item._id || item.petName}
                style={{
                  background: "white",
                  borderRadius: "2rem",
                  overflow: "hidden",
                  boxShadow: "0 12px 35px rgba(0,0,0,0.08)",
                  border: "1.5px solid rgba(124,58,237,0.12)",
                  display: "flex",
                  flexDirection: "column",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                }}
                className="transform hover:-translate-y-1 hover:shadow-xl"
              >
                {/* Photo Viewer with Interactive Toggle */}
                <div style={{ position: "relative", aspectRatio: "4/3", overflow: "hidden", background: "#f3f4f6" }}>
                  <img
                    src={isShowingAfter ? item.afterImage : item.beforeImage}
                    alt={`${item.petName} — ${isShowingAfter ? "After Grooming" : "Before Grooming"}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      transition: "transform 0.4s ease, filter 0.3s ease",
                    }}
                  />

                  {/* Gradient Overlay */}
                  <div style={{
                    position: "absolute",
                    inset: 0,
                    background: "linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.1) 40%, transparent 100%)",
                    pointerEvents: "none",
                  }} />

                  {/* Before / After Switch Pills */}
                  <div style={{
                    position: "absolute",
                    top: "1rem",
                    left: "1rem",
                    display: "flex",
                    background: "rgba(0, 0, 0, 0.65)",
                    backdropFilter: "blur(8px)",
                    borderRadius: "999px",
                    padding: "0.25rem",
                    zIndex: 10,
                    boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                  }}>
                    <button
                      type="button"
                      onClick={() => toggleView(item._id, "before")}
                      style={{
                        padding: "0.3rem 0.8rem",
                        borderRadius: "999px",
                        border: "none",
                        fontSize: "0.68rem",
                        fontWeight: 900,
                        cursor: "pointer",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        transition: "all 0.2s ease",
                        background: !isShowingAfter ? "#F59E0B" : "transparent",
                        color: !isShowingAfter ? "#111827" : "rgba(255,255,255,0.75)",
                        boxShadow: !isShowingAfter ? "0 2px 8px rgba(245,158,11,0.4)" : "none",
                      }}
                    >
                      Before
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleView(item._id, "after")}
                      style={{
                        padding: "0.3rem 0.8rem",
                        borderRadius: "999px",
                        border: "none",
                        fontSize: "0.68rem",
                        fontWeight: 900,
                        cursor: "pointer",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        transition: "all 0.2s ease",
                        background: isShowingAfter ? "linear-gradient(135deg,#10B981,#059669)" : "transparent",
                        color: isShowingAfter ? "white" : "rgba(255,255,255,0.75)",
                        boxShadow: isShowingAfter ? "0 2px 8px rgba(16,185,129,0.4)" : "none",
                      }}
                    >
                      After ✨
                    </button>
                  </div>

                  {/* Pet Tag */}
                  <div style={{
                    position: "absolute",
                    top: "1rem",
                    right: "1rem",
                    background: "rgba(255,255,255,0.92)",
                    backdropFilter: "blur(8px)",
                    borderRadius: "999px",
                    padding: "0.3rem 0.75rem",
                    fontSize: "0.7rem",
                    fontWeight: 800,
                    color: "#7C3AED",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    zIndex: 10,
                  }}>
                    {item.petType === "cat" ? "🐱 Cat" : "🐶 Dog"}
                  </div>

                  {/* Bottom Photo Label */}
                  <div style={{
                    position: "absolute",
                    bottom: "0.85rem",
                    left: "1rem",
                    right: "1rem",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    zIndex: 10,
                    pointerEvents: "none",
                  }}>
                    <span style={{
                      fontSize: "0.72rem",
                      fontWeight: 800,
                      color: isShowingAfter ? "#A7F3D0" : "#FDE68A",
                      background: "rgba(0,0,0,0.5)",
                      backdropFilter: "blur(4px)",
                      padding: "0.2rem 0.6rem",
                      borderRadius: "6px",
                      textTransform: "uppercase",
                    }}>
                      {isShowingAfter ? "✨ Groomed Look" : "📷 Original Look"}
                    </span>
                    <span style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.8)", fontWeight: 600 }}>
                      Tap to switch
                    </span>
                  </div>
                </div>

                {/* Card Information */}
                <div style={{ padding: "1.35rem", display: "flex", flexDirection: "column", flex: 1, justifyContent: "space-between", gap: "0.75rem" }}>
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "0.35rem" }}>
                      <h3 style={{ margin: 0, fontWeight: 900, color: "#111827", fontSize: "1.15rem" }}>{item.petName}</h3>
                      <span style={{ fontSize: "0.8rem", color: "#6B7280", fontWeight: 600 }}>{item.breed}</span>
                    </div>

                    <div style={{ marginBottom: "0.6rem" }}>
                      <span style={{
                        background: "rgba(124,58,237,0.08)",
                        color: "#7C3AED",
                        fontSize: "0.75rem",
                        fontWeight: 800,
                        padding: "0.3rem 0.75rem",
                        borderRadius: "8px",
                        display: "inline-block",
                      }}>
                        ✂️ {item.service}
                      </span>
                    </div>

                    {item.description && (
                      <p style={{ margin: 0, color: "#4B5563", fontSize: "0.82rem", lineHeight: 1.5 }}>
                        {item.description}
                      </p>
                    )}
                  </div>

                  {/* Interactive toggle bar */}
                  <div style={{
                    paddingTop: "0.75rem",
                    borderTop: "1px solid #f3f4f6",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}>
                    <button
                      onClick={() => toggleView(item._id, isShowingAfter ? "before" : "after")}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#7C3AED",
                        fontSize: "0.78rem",
                        fontWeight: 800,
                        cursor: "pointer",
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                        gap: "0.3rem",
                      }}
                    >
                      <span>🔄</span>
                      <span>Switch to {isShowingAfter ? "Before" : "After"}</span>
                    </button>

                    <button
                      onClick={() => setActiveTab("booking")}
                      style={{
                        background: "linear-gradient(135deg,#7C3AED,#EC4899)",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        padding: "0.4rem 0.85rem",
                        fontSize: "0.75rem",
                        fontWeight: 800,
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.05)"; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}
                    >
                      Book Look 🐾
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom Booking CTA */}
        <div style={{ textAlign: "center", marginTop: "3.5rem" }}>
          <p style={{ color: "#6B7280", marginBottom: "1rem", fontSize: "0.95rem" }}>
            Ready to give your pet a stunning fresh look? Book a grooming session today!
          </p>
          <button
            id="gallery-book-btn"
            onClick={() => setActiveTab("booking")}
            style={{
              background: "linear-gradient(135deg,#7C3AED,#EC4899)",
              color: "white",
              padding: "1rem 2.6rem",
              borderRadius: "1rem",
              fontWeight: 900,
              fontSize: "1.05rem",
              border: "none",
              cursor: "pointer",
              boxShadow: "0 8px 30px rgba(124,58,237,0.35)",
              transition: "all 0.3s",
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px) scale(1.04)"; e.currentTarget.style.boxShadow = "0 14px 40px rgba(124,58,237,0.45)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0) scale(1)"; e.currentTarget.style.boxShadow = "0 8px 30px rgba(124,58,237,0.35)"; }}
          >
            Book a Grooming Transformation 🐾
          </button>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .gallery-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 600px) {
          .gallery-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
}

/* ─── Main Component ────────────────────────────────────────────────────── */
const Home = ({ setActiveTab, setServiceSection, setSelectedBookingService }) => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setActiveTestimonial(p => (p + 1) % testimonials.length), 4000);
    return () => clearInterval(id);
  }, []);

  return (
    <div style={{ minHeight: "100vh", overflowX: "hidden" }}>

      {/* ══ HERO ══════════════════════════════════════════════════════════════ */}
      <section style={{ position: "relative", minHeight: "90vh", display: "flex", alignItems: "center", overflow: "hidden", padding: "3rem 1.5rem 3rem" }}>
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, #faf5ff 0%, #fdf2f8 40%, #fff7ed 100%)" }} />
        <div style={{ position: "absolute", top: "-8rem", right: "-8rem", width: "36rem", height: "36rem", borderRadius: "50%", background: "radial-gradient(circle, rgba(168,85,247,0.15) 0%, transparent 70%)" }} />
        <div style={{ position: "absolute", bottom: "-6rem", left: "-6rem", width: "28rem", height: "28rem", borderRadius: "50%", background: "radial-gradient(circle, rgba(236,72,153,0.12) 0%, transparent 70%)" }} />

        {/* Floating SVG decorations */}
        {floatingDefs.map((f, i) => (
          <div key={i} className="animate-float-slow" style={{
            position: "absolute",
            top: `${10 + (i * 11) % 70}%`,
            left: i < 4 ? `${3 + (i * 7) % 12}%` : "auto",
            right: i >= 4 ? `${3 + ((i - 4) * 6) % 10}%` : "auto",
            animationDelay: f.delay,
            pointerEvents: "none",
            color: floatColors[i % 4],
            opacity: 0.12 + (i % 3) * 0.04,
          }}>
            <f.Icon size={f.size} color="currentColor" />
          </div>
        ))}

        {/* Content — split layout */}
        <div className="hero-split-grid" style={{ position: "relative", zIndex: 10, maxWidth: "1200px", margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4rem", alignItems: "center", width: "100%" }}>

          {/* Left — text */}
          <div className="animate-slide-up" style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
            {/* Status badge */}
            <div style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", background: "rgba(255,255,255,0.85)", backdropFilter: "blur(12px)", border: "1.5px solid rgba(168,85,247,0.3)", borderRadius: "999px", padding: "0.4rem 1rem", fontSize: "0.8rem", fontWeight: 700, color: "#7C3AED", width: "fit-content", boxShadow: "0 4px 20px rgba(168,85,247,0.1)" }}>
              <span style={{ width: "0.5rem", height: "0.5rem", background: "#22c55e", borderRadius: "50%", display: "inline-block", animation: "pulse 2s infinite" }} />
              Now Open · Tuesday–Sunday (Monday Closed) · 9 AM–7 PM
            </div>

            {/* Headline */}
            <h1 style={{ fontSize: "clamp(2.8rem, 5vw, 4.5rem)", fontWeight: 900, lineHeight: 1.1, margin: 0, letterSpacing: "-0.02em" }}>
              <span className="animate-shimmer">Furpect Tails</span>
              <br />
              <span style={{ color: "#1f2937" }}>PET GROOMING</span>
              <br />
              <span style={{ background: "linear-gradient(90deg, #EC4899, #F97316)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>&amp; HOTEL</span>
            </h1>

            {/* Tagline */}
            <p style={{ fontSize: "1.2rem", color: "#4B5563", lineHeight: 1.7, margin: 0, maxWidth: "440px", display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.35rem" }}>
              Fresh look, <strong style={{ color: "#7C3AED" }}>FUR-SURE!</strong>
              <PawIcon size={18} color="#7C3AED" />
              Premium grooming &amp; luxury stays for your beloved pets.
            </p>

            {/* CTA buttons */}
            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
              <button
                id="hero-book-btn"
                onClick={() => { if (setSelectedBookingService) setSelectedBookingService(null); setActiveTab("booking"); }}
                className="animate-pulse-glow"
                style={{ background: "linear-gradient(135deg, #7C3AED, #EC4899)", color: "#fff", padding: "1rem 2.2rem", borderRadius: "1rem", fontSize: "1.05rem", fontWeight: 800, border: "none", cursor: "pointer", boxShadow: "0 8px 30px rgba(124,58,237,0.35)", transition: "all 0.3s", display: "flex", alignItems: "center", gap: "0.5rem" }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px) scale(1.04)"; e.currentTarget.style.boxShadow = "0 14px 40px rgba(124,58,237,0.45)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0) scale(1)"; e.currentTarget.style.boxShadow = "0 8px 30px rgba(124,58,237,0.35)"; }}
              >
                Book Appointment <ArrowRightIcon size={18} color="white" />
              </button>
              <button
                id="hero-services-btn"
                onClick={() => {
                  const el = document.getElementById("services");
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                style={{ background: "rgba(255,255,255,0.8)", backdropFilter: "blur(12px)", border: "2px solid rgba(124,58,237,0.25)", color: "#7C3AED", padding: "1rem 2.2rem", borderRadius: "1rem", fontSize: "1.05rem", fontWeight: 800, cursor: "pointer", transition: "all 0.3s", display: "flex", alignItems: "center", gap: "0.5rem" }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.background = "rgba(255,255,255,1)"; e.currentTarget.style.boxShadow = "0 10px 30px rgba(124,58,237,0.15)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.background = "rgba(255,255,255,0.8)"; e.currentTarget.style.boxShadow = "none"; }}
              >
                View Services <SparklesIcon size={16} color="#7C3AED" />
              </button>
            </div>

            {/* Trust badges */}
            <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", paddingTop: "0.5rem" }}>
              {[
                { Icon: StarIcon,        text: "5-Star Rated"    },
                { Icon: CheckCircleIcon, text: "Vetted Staff"     },
                { Icon: MapPinIcon,      text: "Locally Trusted"  },
              ].map(({ Icon, text }) => (
                <span key={text} style={{ fontSize: "0.8rem", fontWeight: 700, color: "#6B7280", display: "flex", alignItems: "center", gap: "0.35rem" }}>
                  <Icon size={15} color="#9333EA" />
                  {text}
                </span>
              ))}
            </div>
          </div>

          {/* Right — Hero image collage */}
          <div className="animate-slide-up-delay-1 hero-image-collage" style={{ position: "relative", height: "520px" }}>
            <div style={{ position: "absolute", top: 0, right: 0, width: "72%", height: "70%", borderRadius: "2rem", overflow: "hidden", boxShadow: "0 30px 80px rgba(0,0,0,0.18)", border: "4px solid white" }}>
              <img src="/img-dog-grooming.png" alt="Dog Grooming" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.4) 0%, transparent 60%)" }} />
              <div style={{ position: "absolute", bottom: "1rem", left: "1rem", color: "white", fontWeight: 800, fontSize: "1rem", display: "flex", alignItems: "center", gap: "0.4rem" }}>
                <PawIcon size={18} color="white" /> Dog Grooming
              </div>
            </div>

            <div style={{ position: "absolute", bottom: "3rem", left: 0, width: "52%", height: "46%", borderRadius: "1.5rem", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", border: "4px solid white" }}>
              <img src="/img-pet-hotel.png" alt="Pet Hotel" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.4) 0%, transparent 60%)" }} />
              <div style={{ position: "absolute", bottom: "0.75rem", left: "0.75rem", color: "white", fontWeight: 800, fontSize: "0.9rem", display: "flex", alignItems: "center", gap: "0.35rem" }}>
                <HotelIcon size={16} color="white" /> Pet Hotel
              </div>
            </div>

            {/* Floating rating card */}
            <div style={{ position: "absolute", top: "42%", left: "34%", background: "white", borderRadius: "1.2rem", padding: "0.8rem 1.2rem", boxShadow: "0 15px 50px rgba(0,0,0,0.15)", display: "flex", alignItems: "center", gap: "0.6rem", zIndex: 10, border: "2px solid rgba(124,58,237,0.1)" }}>
              <StarIcon size={28} color="#FBBF24" filled />
              <div>
                <div style={{ fontWeight: 900, color: "#1f2937", fontSize: "1.1rem", lineHeight: 1 }}>5.0</div>
                <div style={{ fontSize: "0.65rem", color: "#9CA3AF", fontWeight: 600 }}>500+ reviews</div>
              </div>
            </div>

            {/* Open Now pill */}
            <div style={{ position: "absolute", top: "6%", left: "12%", background: "linear-gradient(135deg,#22c55e,#16a34a)", color: "white", borderRadius: "999px", padding: "0.5rem 1rem", fontSize: "0.75rem", fontWeight: 800, boxShadow: "0 8px 25px rgba(34,197,94,0.4)", zIndex: 10, display: "flex", alignItems: "center", gap: "0.4rem" }}>
              <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: "white", opacity: 0.9, display: "inline-block", animation: "pulse 2s infinite" }} />
              Open Now
            </div>
          </div>

        </div>
      </section>



      {/* ══ PET GALLERY ═══════════════════════════════════════════════════════ */}
      <GallerySection setActiveTab={setActiveTab} />

      {/* ══ LOYALTY CARD ═════════════════════════════════════════════════════ */}
      <section style={{ padding: "6rem 1.5rem", background: "white" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ borderRadius: "2rem", overflow: "hidden", boxShadow: "0 30px 80px rgba(124,58,237,0.18)", position: "relative" }}>
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, #5B21B6, #7C3AED, #C026D3, #EC4899)" }} />
            <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle at 10% 50%, rgba(255,255,255,0.08) 0%, transparent 50%), radial-gradient(circle at 90% 20%, rgba(255,255,255,0.05) 0%, transparent 40%)" }} />
            <div style={{ position: "absolute", top: "-5rem", right: "-5rem", width: "20rem", height: "20rem", borderRadius: "50%", border: "2px solid rgba(255,255,255,0.08)" }} />
            <div style={{ position: "absolute", bottom: "-6rem", left: "-6rem", width: "24rem", height: "24rem", borderRadius: "50%", border: "2px solid rgba(255,255,255,0.06)" }} />

            <div className="loyalty-grid" style={{ position: "relative", zIndex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0 }}>
              {/* Left */}
              <div style={{ padding: "3.5rem" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "0.45rem", background: "rgba(250,204,21,0.2)", color: "#FDE047", fontSize: "0.7rem", fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.1em", padding: "0.4rem 0.85rem", borderRadius: "999px", marginBottom: "1.5rem" }}>
                  <GiftIcon size={13} color="#FDE047" /> Rewards Program
                </span>
                <h2 style={{ fontSize: "clamp(1.8rem, 3vw, 2.5rem)", fontWeight: 900, color: "white", margin: "0 0 1rem", lineHeight: 1.2 }}>
                  Earn rewards <br /> every <span style={{ color: "#FDE047" }}>visit!</span>
                </h2>
                <p style={{ color: "rgba(255,255,255,0.75)", lineHeight: 1.7, marginBottom: "2rem", fontSize: "0.95rem" }}>
                  Book grooming sessions and collect stamps. Hit milestones to unlock exclusive perks for your fur baby!
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginBottom: "2rem" }}>
                  {[
                    { Icon: TagIcon,     visits: "5 Visits",  reward: "20% Off your next service", badge: "Milestone", badgeColor: "rgba(250,204,21,0.2)", badgeText: "#FDE047" },
                    { Icon: SparklesIcon, visits: "10 Visits", reward: "FREE Grooming session!",     badge: "Free!",     badgeColor: "rgba(236,72,153,0.25)", badgeText: "#F9A8D4" },
                  ].map((m) => (
                    <div key={m.visits} style={{ display: "flex", alignItems: "center", gap: "0.75rem", background: "rgba(255,255,255,0.1)", borderRadius: "1rem", padding: "0.85rem 1rem" }}>
                      <div style={{ width: "2.4rem", height: "2.4rem", borderRadius: "0.7rem", background: "rgba(255,255,255,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <m.Icon size={18} color="white" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontWeight: 800, color: "white", margin: 0, fontSize: "0.9rem" }}>{m.visits}</p>
                        <p style={{ color: "rgba(255,255,255,0.6)", margin: 0, fontSize: "0.78rem" }}>{m.reward}</p>
                      </div>
                      <span style={{ background: m.badgeColor, color: m.badgeText, fontSize: "0.7rem", fontWeight: 800, padding: "0.25rem 0.6rem", borderRadius: "999px", whiteSpace: "nowrap" }}>{m.badge}</span>
                    </div>
                  ))}
                </div>

                <button
                  id="loyalty-book-btn"
                  onClick={() => { if (setSelectedBookingService) setSelectedBookingService(null); setActiveTab("booking"); }}
                  style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", background: "white", color: "#7C3AED", padding: "0.9rem 2rem", borderRadius: "1rem", fontWeight: 900, fontSize: "1rem", border: "none", cursor: "pointer", boxShadow: "0 8px 30px rgba(0,0,0,0.2)", transition: "all 0.3s" }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.05)"; e.currentTarget.style.boxShadow = "0 14px 40px rgba(0,0,0,0.25)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 8px 30px rgba(0,0,0,0.2)"; }}
                >
                  Book Now <PawIcon size={16} color="#7C3AED" />
                </button>
              </div>

              {/* Right — Loyalty card mockup */}
              <div className="loyalty-card-col" style={{ padding: "3.5rem", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div className="animate-float-med" style={{ width: "100%", maxWidth: "280px" }}>
                  <div style={{ background: "linear-gradient(135deg, #faf5ff, #fdf2f8)", borderRadius: "1.5rem", overflow: "hidden", border: "2.5px dashed rgba(124,58,237,0.4)", boxShadow: "0 30px 70px rgba(0,0,0,0.25)" }}>
                    {/* Card header */}
                    <div style={{ background: "linear-gradient(135deg, #7C3AED, #EC4899)", padding: "1.1rem 1.25rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div>
                        <p style={{ margin: 0, fontSize: "0.6rem", fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.12em", color: "rgba(216,180,254,0.8)" }}>Loyalty Card</p>
                        <p style={{ margin: 0, fontWeight: 800, color: "white", fontSize: "0.9rem" }}>Your Pet's Name</p>
                      </div>
                      <PawIcon size={32} color="rgba(255,255,255,0.8)" />
                    </div>
                    {/* Stamps */}
                    <div style={{ padding: "1.25rem" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "0.4rem" }}>
                        {[...Array(10)].map((_, i) => {
                          const filled = i < 3;
                          const milestone = i === 4 || i === 9;
                          return (
                            <div key={i} style={{
                              aspectRatio: "1", borderRadius: "0.6rem", display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: "0.55rem", fontWeight: 900,
                              border: `2px solid ${filled ? "#7C3AED" : milestone ? "#F9A8D4" : "rgba(124,58,237,0.15)"}`,
                              background: filled ? "#7C3AED" : milestone ? "rgba(249,168,212,0.2)" : "white",
                              color: filled ? "white" : milestone ? "#EC4899" : "rgba(124,58,237,0.25)",
                              boxShadow: filled ? "0 4px 12px rgba(124,58,237,0.35)" : "none",
                            }}>
                              {filled
                                ? <PawIcon size={12} color="white" />
                                : milestone
                                  ? <span style={{ fontSize: "0.52rem", fontWeight: 900 }}>{i === 4 ? "20%" : "Free"}</span>
                                  : <span style={{ fontSize: "0.7rem", opacity: 0.4 }}>○</span>
                              }
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ marginTop: "0.9rem", textAlign: "center" }}>
                        <p style={{ margin: 0, fontSize: "0.65rem", fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.08em", color: "#7C3AED" }}>3/10 Visits</p>
                        <p style={{ margin: "0.2rem 0 0", fontSize: "0.75rem", fontWeight: 700, color: "#6D28D9" }}>2 more visits for 20% Off!</p>
                      </div>
                    </div>
                  </div>
                  <p style={{ textAlign: "center", color: "rgba(255,255,255,0.45)", fontSize: "0.7rem", marginTop: "0.75rem", fontWeight: 600 }}>* Card is linked to your account</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ TESTIMONIALS ═════════════════════════════════════════════════════ */}
      <section style={{ padding: "6rem 1.5rem", background: "#faf5ff" }}>
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "3.5rem" }}>
            <span style={{ display: "inline-block", background: "rgba(124,58,237,0.1)", color: "#7C3AED", fontSize: "0.8rem", fontWeight: 800, padding: "0.4rem 1rem", borderRadius: "999px", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "1rem" }}>Happy Clients</span>
            <h2 style={{ fontSize: "clamp(2rem, 4vw, 2.8rem)", fontWeight: 900, color: "#1f2937", margin: 0, letterSpacing: "-0.02em" }}>
              What Pet Parents <span style={{ background: "linear-gradient(90deg,#7C3AED,#EC4899)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>Are Saying</span>
            </h2>
          </div>

          <div style={{ position: "relative", overflow: "hidden" }}>
            {testimonials.map((t, i) => (
              <div key={i} style={{ display: activeTestimonial === i ? "block" : "none", animation: "fadeIn 0.5s ease forwards" }}>
                <div style={{ background: "white", borderRadius: "2rem", padding: "3rem", boxShadow: "0 20px 60px rgba(124,58,237,0.12)", border: "1px solid rgba(124,58,237,0.1)", textAlign: "center" }}>
                  {/* Avatar */}
                  <div style={{ width: "4.5rem", height: "4.5rem", borderRadius: "50%", background: t.avatarColor, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1.25rem", boxShadow: `0 8px 25px ${t.avatarColor}66` }}>
                    <UserIcon size={28} color="white" />
                  </div>
                  {/* Stars */}
                  <div style={{ display: "flex", justifyContent: "center", gap: "0.2rem", marginBottom: "1.25rem" }}>
                    {[...Array(t.stars)].map((_, s) => <StarIcon key={s} size={18} color="#FBBF24" filled />)}
                  </div>
                  <p style={{ fontSize: "1.1rem", color: "#374151", lineHeight: 1.8, fontStyle: "italic", margin: "0 auto 1.5rem", maxWidth: "600px" }}>
                    "{t.text}"
                  </p>
                  <div>
                    <p style={{ fontWeight: 800, color: "#1f2937", margin: 0, fontSize: "1rem" }}>{t.name}</p>
                    <p style={{ color: "#9CA3AF", margin: 0, fontSize: "0.85rem" }}>{t.pet}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Dots */}
          <div style={{ display: "flex", justifyContent: "center", gap: "0.5rem", marginTop: "1.5rem" }}>
            {testimonials.map((_, i) => (
              <button
                key={i}
                id={`testimonial-dot-${i}`}
                onClick={() => setActiveTestimonial(i)}
                style={{ width: activeTestimonial === i ? "2rem" : "0.6rem", height: "0.6rem", borderRadius: "999px", background: activeTestimonial === i ? "#7C3AED" : "#DDD6FE", border: "none", cursor: "pointer", transition: "all 0.3s ease", padding: 0 }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ══ FINAL CTA ════════════════════════════════════════════════════════ */}
      <section style={{ position: "relative", padding: "7rem 1.5rem", overflow: "hidden", background: "white" }}>
        <div style={{ position: "absolute", top: "-4rem", left: "-4rem", width: "22rem", height: "22rem", borderRadius: "50%", background: "radial-gradient(circle, rgba(124,58,237,0.1) 0%, transparent 70%)" }} />
        <div style={{ position: "absolute", bottom: "-4rem", right: "-4rem", width: "22rem", height: "22rem", borderRadius: "50%", background: "radial-gradient(circle, rgba(236,72,153,0.08) 0%, transparent 70%)" }} />

        <div style={{ position: "relative", zIndex: 1, maxWidth: "700px", margin: "0 auto", textAlign: "center" }}>
          <div className="animate-float-med" style={{ display: "inline-flex", justifyContent: "center", marginBottom: "1.25rem" }}>
            <PawIcon size={60} color="#7C3AED" style={{ opacity: 0.65 }} />
          </div>
          <h2 style={{ fontSize: "clamp(2.2rem, 4vw, 3.2rem)", fontWeight: 900, color: "#1f2937", margin: "0 0 1rem", letterSpacing: "-0.02em", lineHeight: 1.2 }}>
            Ready to pamper <br />
            <span style={{ background: "linear-gradient(90deg, #7C3AED, #EC4899)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>your fur baby?</span>
          </h2>
          <p style={{ color: "#6B7280", fontSize: "1.1rem", marginBottom: "2.5rem", lineHeight: 1.7 }}>
            Book an appointment today and give your pet the royal treatment they deserve.
          </p>
          <button
            id="cta-book-btn"
            onClick={() => { if (setSelectedBookingService) setSelectedBookingService(null); setActiveTab("booking"); }}
            className="animate-pulse-glow"
            style={{ background: "linear-gradient(135deg, #7C3AED, #C026D3, #EC4899)", color: "white", padding: "1.2rem 3rem", borderRadius: "1.2rem", fontSize: "1.2rem", fontWeight: 900, border: "none", cursor: "pointer", boxShadow: "0 15px 40px rgba(124,58,237,0.35)", transition: "all 0.3s", display: "inline-flex", alignItems: "center", gap: "0.6rem" }}
            onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.06) translateY(-3px)"; e.currentTarget.style.boxShadow = "0 22px 55px rgba(124,58,237,0.45)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "scale(1) translateY(0)"; e.currentTarget.style.boxShadow = "0 15px 40px rgba(124,58,237,0.35)"; }}
          >
            Book Appointment Now <PawIcon size={22} color="white" />
          </button>

          <div style={{ marginTop: "1.5rem", display: "flex", justifyContent: "center", gap: "2rem", flexWrap: "wrap" }}>
            {[
              { Icon: LockIcon,     text: "Secure Booking"       },
              { Icon: CalendarIcon, text: "Easy Reschedule"       },
            ].map(({ Icon, text }) => (
              <span key={text} style={{ fontSize: "0.82rem", color: "#9CA3AF", fontWeight: 600, display: "flex", alignItems: "center", gap: "0.35rem" }}>
                <Icon size={14} color="#9CA3AF" />
                {text}
              </span>
            ))}
          </div>
        </div>
      </section>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
};

export default Home;
